import { Banner, Button, Label, TextLink, Typography } from '@neo4j-ndl/react';
import { CheckCircleIconSolid } from '@neo4j-ndl/react/icons';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import type { Instance } from '@nx/state';
import { consoleApi, getApiError, getErrorMessage, useActiveProject } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { useConnect, useNxNavigate } from '@nx/ui';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { constructCustomEndpointUrl } from '../../../../../upx/src/custom-endpoints/helpers';
import { PropertyDisplay } from '../../../../../upx/src/instance/property-display';
import { useModalState } from '../../../log-forwarding/hooks/use-modal-state';
import { isLiveMigration, kindOfMigration } from './common';
import { FinalizeMigrationDialog } from './finalize-migration-dialog';

interface Props {
  originalInstance: Instance;
  migratedInstance: Instance;
}

export const MigrationEvaluationSection = ({ originalInstance, migratedInstance }: Props) => {
  const testMigrationFinalizeModal = useModalState();

  const showTestMigrationFinalizeModal = () => {
    testMigrationFinalizeModal.setError(null);
    testMigrationFinalizeModal.refreshKey();
    testMigrationFinalizeModal.show();
  };

  const navigate = useNavigate();
  const nxNavigate = useNxNavigate();
  const { doDiscoveryAndConnect } = useConnect();
  const activeProject = useActiveProject();

  const handleConnect = async (app: APP_SCOPE) => {
    nxNavigate(app);
    await doDiscoveryAndConnect(migratedInstance);
  };

  const logger = createLogger(APP_SCOPE.ops);
  const [destroy, destroyRes] = consoleApi.useDestroyInstanceMutation();
  const errorMessage = useMemo(() => {
    if (!destroyRes.isError) {
      return null;
    }
    const error = getApiError(destroyRes.error);
    return getErrorMessage(error);
  }, [destroyRes.error, destroyRes.isError]);

  const { data: customEndpointsTarget = [] } = consoleApi.useListCustomEndpointByInstanceQuery(migratedInstance.id);
  const { data: customEndpointsSource = [] } = consoleApi.useListCustomEndpointByInstanceQuery(originalInstance.id);

  const handleDelete = useCallback(
    (instanceToDelete: Instance) => {
      destroy(instanceToDelete.id)
        .unwrap()
        .then(() => {
          testMigrationFinalizeModal.close();
          if (kindOfMigration(migratedInstance) === 'live') {
            void navigate(`/projects/${activeProject.id}/instances`);
          }
        })
        .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
          const error = getApiError(e);
          if (isNotNullish(error.message)) {
            logger.error(error.message);
          }
        });
    },
    [activeProject.id, destroy, logger, migratedInstance, navigate, testMigrationFinalizeModal],
  );

  return (
    <div className="bg-palette-neutral-bg-weak mb-4 mt-3 w-full rounded-3xl">
      {isNotNullish(errorMessage) && <Banner type="danger" description={errorMessage} usage="inline" />}
      <Label className="relative top-[-12px] mr-2" fill="outlined" color={'default'}>
        {kindOfMigration(migratedInstance)} migration
      </Label>
      <div className="display-inline float justify mb-6 flex w-full px-6 pb-2 pt-2">
        <CheckCircleIconSolid className="ndl-icon text-palette-success-icon h-12 w-12" />
        <div className="px-4">
          <b>Target instance is ready for evaluation</b>
          <br />
          <Typography variant="body-small">
            Instance clone:{' '}
            <TextLink
              as="button"
              htmlAttributes={{
                onClick: () => {
                  void handleConnect(APP_SCOPE.query);
                },
              }}
            >
              {migratedInstance.name}
            </TextLink>
          </Typography>
        </div>
        <div className="float-right ml-auto mr-0 flex justify-end">
          <Button
            size="small"
            isDisabled={false}
            color="neutral"
            fill="outlined"
            onClick={showTestMigrationFinalizeModal}
            htmlAttributes={{
              'data-testid': 'finalize-migration',
            }}
          >
            Finalize {kindOfMigration(migratedInstance)} migration
          </Button>
          <FinalizeMigrationDialog
            onAccept={(instanceToDelete) => {
              handleDelete(instanceToDelete);
            }}
            key={testMigrationFinalizeModal.key}
            onDecline={testMigrationFinalizeModal.close}
            open={testMigrationFinalizeModal.visible}
            originalInstance={originalInstance}
            migratedInstance={migratedInstance}
          />
        </div>
      </div>
      <div className="ndl-divider ndl-divider-horizontal mb-6 px-6" role="separator" aria-orientation="horizontal">
        <div className="flex pb-2 pt-4">
          <Typography variant={'h6'}>
            Next steps before finalizing the {kindOfMigration(migratedInstance)} migration{' '}
          </Typography>
          <div className="px-2">
            {/* TODO: actual link*/}
            <TextLink type="external" href="https://neo4j.com/docs/aura/tutorials/upgrade/">
              Docs
            </TextLink>
          </div>
        </div>
        <div className="pb-4">
          <ol className="list-decimal pl-6">
            {isLiveMigration(migratedInstance) && (
              <li className="mb-4">
                Prepare your instance to be used by the application. This can include restoring the configurations for
                <ul className="list-inside list-disc">
                  <li>Role-based access control</li>
                  <li>Private link</li>
                  <li>Log forwarding on Business Critical instances (if required)</li>
                  <li>Customer managed encryption keys</li>
                </ul>
              </li>
            )}
            {isLiveMigration(migratedInstance) && (
              <li className="mb-4">
                Verify that all your indexes have migrated and are online. Validate that the types are as required,
                recreating as required.{' '}
                <TextLink
                  type="external"
                  href="https://neo4j.com/docs/upgrade-migration-guide/current/version-5/migration/planning/#_choosing_a_replacement_index"
                >
                  Docs
                </TextLink>
              </li>
            )}
            {isLiveMigration(migratedInstance) && (
              <li className="mb-4">
                <div className="mb-2">Move your application to use the new instance.</div>
                <div className="mb-2">
                  If you are not using a custom endpoint, update the connection URIs to point to the new instance.
                </div>
                <div className="mb-2">
                  <PropertyDisplay
                    label="Connection URI for migrated instance"
                    text={migratedInstance.boltUrl}
                    allowCopyText
                  />
                </div>
                <div className="mb-2">
                  If you are using a custom endpoint, repoint your custom endpoint to the new instance.
                  {customEndpointsSource.map((ep) => (
                    <PropertyDisplay
                      label="Custom endpoint on source instance"
                      text={constructCustomEndpointUrl(ep)}
                      allowCopyText
                      key={ep.id}
                    />
                  ))}
                  {customEndpointsTarget.map((ep) => (
                    <PropertyDisplay
                      label="Custom endpoint on migrated instance"
                      text={constructCustomEndpointUrl(ep)}
                      allowCopyText
                      key={ep.id}
                    />
                  ))}
                  <TextLink type="external" href="https://neo4j.com/docs/aura/managing-databases/custom-endpoints/">
                    Docs
                  </TextLink>
                </div>
              </li>
            )}
            {isLiveMigration(migratedInstance) && (
              <li className="mb-4">
                Verify that the application works as expected. If you have set the application to read-only mode, this
                can be enabled for writes once you have validated read activity as expected.
              </li>
            )}
            {isLiveMigration(migratedInstance) && (
              <li className="mb-4">
                Once you have completed testing and cutover for the application(s), click the “Finalize live migration”
                button.{' '}
              </li>
            )}
            {!isLiveMigration(migratedInstance) && (
              <li className="mb-4">
                Prepare your instance to be used by your tests and non-production application. This can include
                restoring the configurations for
                <ul className="list-inside list-disc">
                  <li>Role-based access control</li>
                  <li>Private link</li>
                  <li>Log forwarding on Business Critical instances (if required)</li>
                  <li>Customer managed encryption keys</li>
                </ul>
              </li>
            )}
            {!isLiveMigration(migratedInstance) && (
              <li className="mb-4">
                Verify that all your indexes have migrated and are online. Validate that the types are as required,
                recreating as required.{' '}
                <TextLink
                  type="external"
                  href="https://neo4j.com/docs/upgrade-migration-guide/current/version-5/migration/planning/#_choosing_a_replacement_index"
                >
                  Docs
                </TextLink>
              </li>
            )}
            {!isLiveMigration(migratedInstance) && (
              <li className="mb-4">
                Run a full or representative test workload against the test instance, validating that the behaviour and
                test results are as expected.
              </li>
            )}
            {!isLiveMigration(migratedInstance) && (
              <li className="mb-4">
                Once you have completed testing and a ready to remove the test instance, click the “Finalize test
                migration” button. The test migration can be repeated as many times as needed until you are happy that
                everything is ready for the live migration.
              </li>
            )}
          </ol>
        </div>
      </div>
    </div>
  );
};
